// coffee-caffeine.js
import React, { useState, useEffect } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "Kalkulator Tingkat Konsumsi Kopi Harian - Hitung Jumlah Kafein yang Aman";
const DESCRIPTION =
  "Gunakan kalkulator tingkat konsumsi kopi harian untuk mengetahui jumlah kafein yang tepat untuk Anda berdasarkan berat badan dan jenis kopi. Hitung dosis aman dan hindari risiko kelebihan kafein.";
const KEYWORDS =
  "kalkulator kopi, jumlah kafein harian, hitung kafein dalam kopi, tingkat konsumsi kopi, bahaya kafein, manfaat kopi, caffeine calculator, kopi arab, espresso";
const OG_URL = "https://do-calculate.com/calculator/id/coffee-caffeine/";

const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Kalkulator Tingkat Konsumsi Kopi Harian",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "HealthApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Beranda",
        item: "https://do-calculate.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Kalkulator Kesehatan",
        item: "https://do-calculate.com/id/all-calculators/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Kalkulator Tingkat Konsumsi Kopi",
        item: OG_URL,
      },
    ],
  },
];

const BEVERAGES = {
  espresso: { name: "Espresso (30 ml)", caffeine: 63, unit: "cangkir kecil" },
  latte: { name: "Latte / Cappuccino (240 ml)", caffeine: 77, unit: "gelas" },
  brewed: { name: "Kopi Seduh / Amerika (240 ml)", caffeine: 96, unit: "gelas" },
  instant: { name: "Kopi Instan (240 ml)", caffeine: 62, unit: "gelas" },
  turkish: { name: "Kopi Turki (60 ml)", caffeine: 50, unit: "cangkir kecil" },
  saudi: { name: "Kopi Arab (cangkir kecil)", caffeine: 15, unit: "cangkir kecil" },
  black_tea: { name: "Teh Hitam (240 ml)", caffeine: 47, unit: "gelas" },
  green_tea: { name: "Teh Hijau (240 ml)", caffeine: 28, unit: "gelas" },
  energy_drink: { name: "Minuman Energi (250 ml)", caffeine: 80, unit: "kaleng" },
  cola: { name: "Minuman Bersoda Cola (355 ml)", caffeine: 34, unit: "kaleng" },
};

function CoffeeCaffeineCalculator() {
  const [weight, setWeight] = useState("");
  const [beverageType, setBeverageType] = useState("espresso");
  const [dailyCups, setDailyCups] = useState(1);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const calculateCaffeine = () => {
    try {
      setError("");
      const w = parseFloat(weight);
      const cups = parseFloat(dailyCups);

      if (!w || w <= 0) {
        setResult(null);
        return;
      }

      const safeLimit = 400; 
      const selectedBeverage = BEVERAGES[beverageType];
      const caffeinePerCup = selectedBeverage.caffeine;
      const totalConsumed = caffeinePerCup * cups;

      const cupsToLimit = Math.floor(safeLimit / caffeinePerCup);
      const lethalDoseMg = w * 150;
      const cupsToLethal = Math.floor(lethalDoseMg / caffeinePerCup);

      let status = "";
      let statusColor = "";
      if (totalConsumed <= 200) {
        status = "Konsumsi aman dan rendah";
        statusColor = "#1a5f3f";
      } else if (totalConsumed <= 400) {
        status = "Konsumsi sedang (dalam batas yang direkomendasikan)";
        statusColor = "#856404";
      } else {
        status = "Konsumsi tinggi (melebihi batas aman harian)";
        statusColor = "#721c24";
      }

      setResult({
        totalConsumed,
        safeLimit,
        cupsToLimit,
        cupsToLethal,
        status,
        statusColor,
        beverageName: selectedBeverage.name,
        unit: selectedBeverage.unit,
      });
    } catch (err) {
      setError("Terjadi kesalahan dalam perhitungan. Harap periksa data.");
    }
  };

  useEffect(() => {
    calculateCaffeine();
  }, [weight, beverageType, dailyCups]);

  const handleClear = () => {
    setWeight("");
    setBeverageType("espresso");
    setDailyCups(1);
    setResult(null);
    setError("");
  };

  return (
    <div className="ltr" >
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={SCHEMA}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ padding: "15px", borderRadius: "5px" }}
          >
            <h1 style={{ fontSize: "22px", margin: 0 }}>Kalkulator Tingkat Konsumsi Kopi Harian</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                <strong>Kalkulator Tingkat Konsumsi Kopi</strong> membantu Anda melacak jumlah kafein yang Anda konsumsi setiap hari. 
                Otoritas Makanan dan Obat Saudi (SFDA) merekomendasikan agar orang dewasa yang sehat tidak melebihi konsumsi kafein 
                <strong> 400 mg </strong> per hari. Masukkan berat badan Anda dan jenis minuman favorit Anda untuk mengetahui batas aman Anda.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />

            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>Data Konsumsi</h2>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <label >Berat Badan (kg)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    placeholder="Contoh: 70"
                  />
                </div>
                <div className="col-md-6">
                  <label >Jenis Minuman</label>
                  <select
                    className="form-select"
                    value={beverageType}
                    onChange={(e) => setBeverageType(e.target.value)}
                  >
                    {Object.entries(BEVERAGES).map(([key, value]) => (
                      <option key={key} value={key}>
                        {value.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <label >Jumlah Gelas/Cangkir per Hari</label>
                  <input
                    type="number"
                    className="form-control"
                    value={dailyCups}
                    onChange={(e) => setDailyCups(e.target.value)}
                    min="1"
                  />
                </div>
              </div>

              <div className="text-center mb-3 mt-4">
                <button
                  onClick={handleClear}
                  className="btn btn-secondary"
                  style={{ fontSize: "16px", padding: "10px 30px" }}
                >
                  Hapus Data
                </button>
              </div>

              {error && (
                <div className="alert alert-warning" role="alert">
                  {error}
                </div>
              )}

              {result && (
                <div className="mt-4">
                  <h3 style={{ fontSize: "18px", marginBottom: "15px", textAlign: "center" }}>
                    <strong>Hasil:</strong>
                  </h3>
                  <table className="table bmi-table" style={{ width: "100%" }}>
                    <thead>
                      <tr>
                        <th className="bmi-header" style={{ textAlign: "center" }}>Keterangan</th>
                        <th className="bmi-header" style={{ textAlign: "center" }}>Nilai</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td style={{ textAlign: "center" }}>Total Kafein yang Dikonsumsi</td>
                        <td style={{ textAlign: "center", fontWeight: "bold", color: result.statusColor }}>
                          {result.totalConsumed} mg
                         </td>
                      </tr>
                      <tr>
                        <td style={{ textAlign: "center" }}>Status</td>
                        <td style={{ textAlign: "center", fontWeight: "bold", color: result.statusColor }}>
                          {result.status}
                         </td>
                      </tr>
                      <tr>
                        <td style={{ textAlign: "center" }}>Batas Maksimum Aman per Hari</td>
                        <td style={{ textAlign: "center" }}>400 mg</td>
                      </tr>
                      <tr>
                        <td style={{ textAlign: "center" }}>Jumlah {result.unit} yang Diizinkan untuk Menghindari Gejala</td>
                        <td style={{ textAlign: "center", fontWeight: "bold" }}>
                          {result.cupsToLimit} {result.unit}
                         </td>
                      </tr>
                    </tbody>
                  </table>

                  <div className="info-sections-container mt-4">
                    <div className="info-section">
                      <div className="info-section-header"><strong>Peringatan Dosis Berbahaya</strong></div>
                      <div className="info-section-body">
                        <p>Berdasarkan berat badan Anda ({weight} kg), jumlah kafein bisa menjadi sangat berbahaya atau mematikan jika mencapai konsumsi:</p>
                        <h4 className="text-danger text-center">{result.cupsToLethal} {result.unit}</h4>
                        <p className="small text-muted text-center">* Angka ini hanya perkiraan untuk tujuan pendidikan, sensitivitas terhadap kafein bervariasi dari orang ke orang.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <hr />
            <RelatedLinks />
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>

      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                <strong>Panduan Konsumsi Kafein yang Aman</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  Kafein adalah stimulan alami yang ditemukan dalam kopi, teh, dan cokelat. Sementara memberikan kewaspadaan dan energi, konsumsi berlebihan menyebabkan efek samping.
                </p>
                <h3 style={{ fontSize: "17px" }}>Batas yang Direkomendasikan (menurut Otoritas Makanan dan Obat Saudi):</h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li><strong>Orang Dewasa Sehat:</strong> maksimum 400 mg (setara dengan 4 gelas kopi seduh).</li>
                  <li><strong>Ibu Hamil dan Menyusui:</strong> konsumsi tidak boleh melebihi 200 mg per hari.</li>
                  <li><strong>Anak-anak dan Remaja:</strong> disarankan untuk mengurangi konsumsi seminimal mungkin (tidak melebihi 2,5 mg per kg berat badan).</li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>Gejala Kelebihan Dosis Kafein</strong>
              </div>
              <div className="info-section-body">
                <p>Jika Anda melebihi batas yang diizinkan, Anda mungkin merasakan gejala berikut:</p>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>Insomnia dan kesulitan tidur.</li>
                  <li>Detak jantung cepat.</li>
                  <li>Kecemasan dan ketegangan saraf.</li>
                  <li>Gangguan pencernaan.</li>
                  <li>Sakit kepala dan tremor.</li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>Kandungan Kafein dalam Minuman Umum</strong>
              </div>
              <div className="info-section-body">
                <table className="table bmi-table">
                  <thead>
                    <tr>
                      <th className="bmi-header" style={{ textAlign: "center" }}>Minuman</th>
                      <th className="bmi-header" style={{ textAlign: "center" }}>Jumlah Kafein (perkiraan)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td style={{ textAlign: "center" }}>Cangkir Kopi Arab</td><td style={{ textAlign: "center" }}>15 mg</td></tr>
                    <tr><td style={{ textAlign: "center" }}>Gelas Kopi Amerika (240 ml)</td><td style={{ textAlign: "center" }}>95-165 mg</td></tr>
                    <tr><td style={{ textAlign: "center" }}>Shot Espresso (30 ml)</td><td style={{ textAlign: "center" }}>63 mg</td></tr>
                    <tr><td style={{ textAlign: "center" }}>Gelas Teh Hitam</td><td style={{ textAlign: "center" }}>47 mg</td></tr>
                    <tr><td style={{ textAlign: "center" }}>Kaleng Minuman Energi</td><td style={{ textAlign: "center" }}>80 mg</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CoffeeCaffeineCalculator;